// src/components/Layout.js — ACTUALIZADO 11 abril 2026: Chat nav + Notification badge + Selector idioma
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth, ROL, guardarIdiomaPerfil } from '../contexts/AuthContext';
import { useLang } from '../i18n';
import Icon from './Icons';

const NAV_MASTER = [
  { path:'/dashboard',     icon:'home',         key:'tab_inicio' },
  { path:'/candidaturas',  icon:'candidaturas',  key:'tab_candidaturas' },
  { path:'/evaluadores',   icon:'evaluadores',   key:'tab_evaluadores' },
  { path:'/gala',          icon:'gala',          key:'tab_gala' },
  { path:'/titularidades', icon:'trophy',        key:'tab_titularidades' },
  { path:'/chat',          icon:'chat',          key:'tab_chat_label' },
  { path:'/notificaciones',icon:'notif',         key:'tab_notif' },
  { path:'/config',        icon:'config',        key:'tab_config' },
  { path:'/recursos',     icon:'doc',           key:'tab_recursos' },
];
const NAV_CIUDAD = [
  { path:'/ciudad',        icon:'home',          key:'tab_candidatura' },
  { path:'/notificaciones',icon:'notif',         key:'tab_notif' },
  { path:'/perfil',        icon:'user',          key:'perfil_titulo' },
];
const NAV_EVAL = [
  { path:'/evaluador',     icon:'home',          key:'tab_inicio' },
  { path:'/recursos',      icon:'doc',           key:'tab_recursos' },
  { path:'/notificaciones',icon:'notif',         key:'tab_notif' },
  { path:'/perfil',        icon:'user',          key:'perfil_titulo' },
];

// ============================================================
// SELECTOR DE IDIOMA — Componente reutilizable
// ============================================================
function LangSelector({ compact = false }) {
  const { lang, setLang: setLangLocal, LANGS, t } = useLang();
  const { uid } = useAuth() || {};
  // P0-EVAL: con sesión, el idioma se guarda también en el perfil (idioma + prefs.idioma).
  // Antes solo iba a localStorage y el listener del doc users lo revertía.
  const setLang = code => { setLangLocal(code); if (uid) guardarIdiomaPerfil(uid, code); };
  return (
    <div style={{ position:'relative', display:'inline-block' }}>
      <select
        value={lang}
        onChange={e => setLang(e.target.value)}
        aria-label={t?.('selector_idioma') || 'Idioma'}
        style={{
          appearance:'none', WebkitAppearance:'none',
          background: compact ? 'transparent' : 'var(--bg-elevated)',
          border: compact ? 'none' : '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          color:'var(--cyan)',
          fontSize: compact ? 11 : 12,
          fontWeight:700,
          fontFamily:'var(--font-display)',
          padding: compact ? '4px 22px 4px 6px' : '6px 28px 6px 10px',
          cursor:'pointer',
          letterSpacing:'0.05em',
        }}
      >
        {LANGS.map(l => (
          <option key={l.code} value={l.code}>
            {l.flag} {compact ? l.code.toUpperCase().replace('-BR','') : l.name}
          </option>
        ))}
      </select>
      <span style={{
        position:'absolute', right: compact ? 4 : 8, top:'50%', transform:'translateY(-50%)',
        pointerEvents:'none', color:'var(--cyan)', fontSize:9, opacity:0.6,
      }}>&#9662;</span>
    </div>
  );
}

export { LangSelector };

export function Layout({ children, modoEval, onToggleModo }) {
  const { pathname } = useLocation();
  const { usuario, rol, logout, uid } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Listen for unread notifications
  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, 'notifications'), where('uid', '==', uid), where('leida', '==', false));
    const unsub = onSnapshot(q, snap => setUnreadCount(snap.size), () => {});
    return unsub;
  }, [uid]);

  const navItems = (rol === ROL.MASTER && modoEval) ? NAV_EVAL 
                 : rol === ROL.MASTER ? NAV_MASTER 
                 : rol === ROL.CIUDAD ? NAV_CIUDAD 
                 : NAV_EVAL;
  const homePath = rol === ROL.CIUDAD ? '/ciudad' : rol === ROL.EVAL ? '/evaluador' : '/dashboard';
  const handleLogout = async () => { await logout(); navigate('/login'); };
  const initial = (usuario?.nombre || 'U').charAt(0).toUpperCase();

  const rolLabel = rol === ROL.MASTER ? (t('rol_master')||'MASTER ACES')
                 : rol === ROL.EVAL   ? (t('rol_evaluador')||'EVALUADOR')
                 : (t('rol_ciudad')||'CIUDAD');
  const rolColor = rol === ROL.MASTER ? 'var(--amber)' : rol === ROL.EVAL ? 'var(--cyan)' : 'var(--blue)';

  const NavItem = ({ item }) => {
    const active = item.path === homePath ? pathname === item.path : pathname.startsWith(item.path);
    const isNotif = item.path === '/notificaciones';
    return (
      <Link to={item.path} onClick={() => setSidebarOpen(false)} className={active ? 'nav-active' : ''} style={{
        display:'flex', alignItems:'center', gap:10,
        padding:'9px 12px', borderRadius:'var(--radius-sm)', marginBottom:2,
        color: active ? 'var(--text)' : 'var(--text-secondary)',
        background: active ? 'rgba(0,207,200,0.06)' : 'transparent',
        fontWeight: active ? 700 : 400, fontSize:13,
        fontFamily:'var(--font-display)', textDecoration:'none',
        transition:'all 200ms cubic-bezier(0.16, 1, 0.3, 1)', position:'relative',
      }}
        onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'var(--bg-elevated)'; e.currentTarget.style.color = 'var(--text)'; }}}
        onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}}
      >
        <div style={{
          width:28, height:28, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center',
          background: active ? 'rgba(0,207,200,0.12)' : 'transparent',
          transition:'background 200ms ease',
        }}>
          <Icon name={item.icon} size={16} color={active ? 'var(--cyan)' : 'var(--text-muted)'} />
        </div>
        {t(item.key) || item.key}
        {isNotif && unreadCount > 0 && (
          <span style={{
            marginLeft:'auto', minWidth:18, height:18, borderRadius:9,
            background:'var(--red)', color:'#fff', fontSize:10, fontWeight:700,
            display:'flex', alignItems:'center', justifyContent:'center',
            padding:'0 5px', fontFamily:'var(--font-display)',
            boxShadow:'0 0 8px rgba(229,57,53,0.4)',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'var(--bg-base)' }}>
      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <button type="button" className="sidebar-overlay" onClick={() => setSidebarOpen(false)}
          aria-label={t('cerrar_menu')}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:199, display:'none', border:'none', cursor:'pointer' }} />
      )}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`} style={{
        width:'var(--sidebar-w)', flexShrink:0, background:'var(--bg-sidebar)',
        borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column',
        position:'fixed', top:0, left:0, bottom:0, zIndex:200,
      }}>
        {/* HEADER: Logo + selector idioma */}
        <div style={{
          padding:'14px 16px 10px',
          borderBottom:'1px solid var(--border)',
        }}>
          <Link to={homePath} style={{
            textDecoration:'none', display:'flex', alignItems:'center', gap:10,
          }}>
            <img src="/logo_cities.png" alt="ACES Cities Network"
                 style={{ height:32, width:'auto', objectFit:'contain' }}
                 onError={e => { e.target.style.display='none'; }}/>
          </Link>
          {/* Selector de idioma debajo del logo */}
          <div style={{ marginTop:10 }}>
            <LangSelector />
          </div>
        </div>

        <nav style={{ flex:1, padding:'10px 8px', overflowY:'auto' }}>
          {navItems.map(item => <NavItem key={item.path} item={item} />)}
        </nav>
        <div style={{ padding:'10px 8px', borderTop:'1px solid var(--border)' }}>
          <Link to="/perfil" style={{
            display:'flex', alignItems:'center', gap:10, padding:'8px 12px',
            borderRadius:'var(--radius-sm)', textDecoration:'none',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{
              width:32, height:32, borderRadius:'50%', flexShrink:0,
              background:rolColor+'22', border:'1px solid '+rolColor+'44',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:13, fontWeight:700, color:rolColor, fontFamily:'var(--font-display)',
            }}>{initial}</div>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text)', overflow:'hidden',
                            textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {usuario?.nombre} {usuario?.apellidos}
              </div>
              <div style={{ fontSize:9, color:rolColor, fontWeight:700, letterSpacing:'0.06em' }}>{rolLabel}</div>
            </div>
          </Link>
                {rol === ROL.MASTER && onToggleModo && (
        <button onClick={onToggleModo} style={{
          width:'100%', display:'flex', alignItems:'center', gap:8,
          padding:'7px 12px', borderRadius:'var(--radius-sm)', marginBottom:4,
          background: modoEval ? 'var(--amber-soft)' : 'transparent',
          color: modoEval ? 'var(--amber)' : 'var(--cyan)',
          fontSize:12, fontFamily:'var(--font-display)', cursor:'pointer',
          border:'none', borderTop:'1px solid var(--border)',
        }}>
          {modoEval ? (t('modo_master')||'Modo master') : (t('modo_evaluador')||'Modo evaluador')}
        </button>
      )}
      <button onClick={handleLogout} style={{
            width:'100%', display:'flex', alignItems:'center', gap:8,
            padding:'7px 12px', borderRadius:'var(--radius-sm)', marginTop:2,
            background:'transparent', color:'var(--text-muted)', fontSize:12,
            fontFamily:'var(--font-display)', cursor:'pointer', border:'none',
          }}
            onMouseEnter={e => { e.currentTarget.style.background='var(--red-soft)'; e.currentTarget.style.color='var(--red)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--text-muted)'; }}
          >
            <Icon name="arrowLeft" size={14} /> {t('auth_cerrar_sesion')||'Cerrar sesion'}
          </button>
        </div>
      </aside>

      <main className="main-content" style={{
        flex:1, marginLeft:'var(--sidebar-w)', minHeight:'100vh', display:'flex', flexDirection:'column',
      }}>
        <header className="topbar-mobile" style={{
          height:'var(--topbar-h)', background:'var(--bg-header)',
          borderBottom:'1px solid var(--border)', display:'none',
          alignItems:'center', justifyContent:'space-between',
          padding:'0 16px', position:'sticky', top:0, zIndex:50,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button onClick={() => setSidebarOpen(v => !v)} style={{
              width:36, height:36, borderRadius:8, background:'transparent',
              border:'1px solid var(--border)', display:'flex', alignItems:'center',
              justifyContent:'center', color:'var(--text-secondary)', cursor:'pointer',
            }}>
              <Icon name="menu" size={18} />
            </button>
            <Link to={homePath}>
              <img src="/logo_cities.png" alt="ACES Cities Network" style={{ height:28, width:'auto' }}/>
            </Link>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {/* Selector idioma compacto en mobile topbar */}
            <LangSelector compact />
            <Link to="/perfil">
              <div style={{ width:32, height:32, borderRadius:'50%', background:rolColor+'22',
                            border:'1px solid '+rolColor+'44', display:'flex', alignItems:'center',
                            justifyContent:'center', fontSize:13, fontWeight:700, color:rolColor }}>
                {initial}
              </div>
            </Link>
          </div>
        </header>
        <div className="main-content-inner" style={{ flex:1, padding:'24px', paddingBottom:80 }}>{children}</div>
      </main>

      <nav className="bottom-nav" style={{
        position:'fixed', bottom:0, left:0, right:0, height:60,
        background:'var(--bg-sidebar)', borderTop:'1px solid var(--border)',
        display:'none', alignItems:'center', zIndex:100, padding:'0 8px',
      }}>
        {navItems.slice(0,5).map(item => {
          const active = item.path === homePath ? pathname === item.path : pathname.startsWith(item.path);
          const isNotif = item.path === '/notificaciones';
          return (
            <Link key={item.path} to={item.path} style={{
              display:'flex', flexDirection:'column', alignItems:'center', gap:2,
              padding:'6px 8px', flex:1, textDecoration:'none',
              color: active ? 'var(--cyan)' : 'var(--text-muted)',
              position:'relative',
            }}>
              <div style={{ position:'relative' }}>
                <Icon name={item.icon} size={20} color={active ? 'var(--cyan)' : 'var(--text-muted)'} />
                {isNotif && unreadCount > 0 && (
                  <span style={{
                    position:'absolute', top:-4, right:-8,
                    minWidth:14, height:14, borderRadius:7,
                    background:'var(--red)', color:'#fff', fontSize:8, fontWeight:700,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    padding:'0 3px',
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <span style={{ fontSize:9, fontWeight:active?600:400, fontFamily:'var(--font-display)' }}>
                {t(item.key)}
              </span>
            </Link>
          );
        })}
      </nav>

      <style>{`
        :root { --sidebar-w: 220px; --topbar-h: 56px; }
        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-100%);
            transition: transform 250ms cubic-bezier(0.16, 1, 0.3, 1);
          }
          .sidebar.sidebar-open {
            transform: translateX(0);
          }
          .sidebar-overlay {
            display: block !important;
          }
          .main-content { margin-left: 0 !important; }
          .topbar-mobile { display: flex !important; }
          .bottom-nav { display: flex !important; }
          .main-content-inner { padding: 14px !important; padding-bottom: 80px !important; }
          /* Tables scroll horizontally */
          .data-table { display: block; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        }
        @media (min-width: 769px) {
          .sidebar-overlay { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export function PageHeader({ title, subtitle, action, back, badge }) {
  const navigate = useNavigate();
  return (
    <div className="section-fade" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between',
                  marginBottom:24, gap:12, flexWrap:'wrap' }}>
      <div style={{ display:'flex', alignItems:'center', gap:14 }}>
        {back && (
          <button onClick={() => navigate(-1)} style={{
            width:38, height:38, borderRadius:10,
            background:'linear-gradient(135deg, var(--bg-elevated) 0%, var(--bg-card) 100%)',
            border:'1px solid var(--border)', display:'flex', alignItems:'center',
            justifyContent:'center', color:'var(--text-secondary)', cursor:'pointer',
            transition:'all 150ms ease',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-active)'; e.currentTarget.style.color = 'var(--text)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            <Icon name="chevronLeft" size={18} />
          </button>
        )}
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <h1 style={{ fontSize:22, fontWeight:800, fontFamily:'var(--font-display)',
                         letterSpacing:'-0.02em', lineHeight:1.2 }}>{title}</h1>
            {badge}
          </div>
          {subtitle && <p style={{ fontSize:13, color:'var(--text-secondary)', marginTop:4, fontWeight:500 }}>{subtitle}</p>}
        </div>
      </div>
      {action && <div style={{ flexShrink:0 }}>{action}</div>}
    </div>
  );
}
